import { TextPill } from '../FbUI/TextPill'
import { colors } from '../../styles/tokens'

/**
 * Color mappings for sample request status pills.
 *
 * Follows the DS2026 Figma badge palette — 50/700 token pairs (bg-subtle +
 * strong-text) for accessible AA contrast on the subtle variant, and
 * strong/700 backgrounds with white text on the solid variant.
 *
 * "solid" variant: white text on a strong-colored background (preview headers).
 * "subtle" variant: dark text on a muted background (tables and cards).
 */
const SOLID_COLORS: Record<string, { bg: string; text: string }> = {
  submitted: { bg: colors['info-strong'], text: colors['fg-inverse'] },
  pending_review: { bg: colors['warning-strong'], text: colors['fg-inverse'] },
  accepted: { bg: colors['success-strong'], text: colors['fg-inverse'] },
  rejected: { bg: colors['error-strong'], text: colors['fg-inverse'] },
  cancelled: { bg: colors['neutral-700'], text: colors['fg-inverse'] },
  // FIR-7033: manual fulfillment stages — sent (in transit), delivered (done).
  sent: { bg: colors['info-strong'], text: colors['fg-inverse'] },
  delivered: { bg: colors['success-strong'], text: colors['fg-inverse'] },
}

const SUBTLE_COLORS: Record<string, { bg: string; text: string }> = {
  submitted: {
    bg: colors['info-bg-subtle'],
    text: colors['info-strong'],
  },
  pending_review: {
    bg: colors['warning-bg-subtle'],
    text: colors['warning-strong'],
  },
  accepted: {
    bg: colors['success-bg-subtle'],
    text: colors['success-strong'],
  },
  rejected: {
    bg: colors['error-bg-subtle'],
    text: colors['error-strong'],
  },
  cancelled: {
    bg: colors['neutral-100'],
    text: colors['neutral-700'],
  },
  // FIR-7033: manual fulfillment stages — sent (in transit), delivered (done).
  sent: {
    bg: colors['info-bg-subtle'],
    text: colors['info-strong'],
  },
  delivered: {
    bg: colors['success-bg-subtle'],
    text: colors['success-strong'],
  },
}

const DEFAULT_COLOR = {
  bg: colors['neutral-100'],
  text: colors['neutral-700'],
}

interface SampleStatusBadgeProps {
  status: string
  label: string
  variant?: 'solid' | 'subtle'
  className?: string
  style?: React.CSSProperties
}

/**
 * Reusable status badge for sample requests.
 *
 * @param status - The raw status value (e.g. "submitted", "pending_review")
 * @param label - Display text (e.g. status_display from the API)
 * @param variant - "solid" for bold badges (headers), "subtle" for muted (tables/cards)
 */
export function SampleStatusBadge({
  status,
  label,
  variant = 'subtle',
  className,
  style,
}: SampleStatusBadgeProps) {
  const palette = variant === 'solid' ? SOLID_COLORS : SUBTLE_COLORS
  const color = palette[status] || DEFAULT_COLOR

  return (
    <TextPill
      backgroundColor={color.bg}
      textColor={color.text}
      className={className}
      style={style}
    >
      {label}
    </TextPill>
  )
}
